import {
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  DrawerProps,
  IconButton,
  List,
  Stack,
  StackProps,
  Typography,
} from '@mui/material';
import { UploadThumbnail } from '@subflow-frontend/sections/@dashboard/file/portal/UploadThumbnail';
import React, { useState } from 'react';
import { IFile } from '../../../../@types/file';
import Scrollbar from '../../../../components/Scrollbar';
import FileThumbnail, {
  fileFormat,
} from '../../../../components/file-thumbnail';
import Iconify from '../../../../components/iconify';
import { fData } from '../../../../utils/formatNumber';
import { fDateTime } from '../../../../utils/formatTime';
import FileInvitedItem from '../FileInvitedItem';
import FileShareDialog from './FileShareDialog';
import RoleBasedGuard from '@subflow-frontend/guards/RoleBasedGuard';
import { InviteMemberRequestDtoRoleEnum } from '@subflow/api-client';
import { accessTypesById } from '@subflow-frontend/sections/content/library/types';

interface Props<ID extends string | number = number> extends DrawerProps {
  item: IFile<ID>;
  favorited?: boolean;
  //
  onFavorite?: VoidFunction;
  onCopyLink: VoidFunction;
  //
  onClose: VoidFunction;
  onDelete?: () => Promise<void>;
  onEdit?: () => void;
}

const FileDetailsDrawer = <ID extends string | number = number>({
  item,
  open,
  favorited,
  //
  onFavorite,
  onCopyLink,
  onClose,
  onDelete,
  onEdit,
  ...other
}: Props<ID>) => {
  const {
    name,
    description,
    size,
    url,
    type,
    shared,
    dateModified,
    published,
    accessType,
  } = item;
  const [openShare, setOpenShare] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [toggleProperties, setToggleProperties] = useState(true);

  const hasShared = shared && !!shared.length;

  const handleToggleProperties = () => {
    setToggleProperties(!toggleProperties);
  };

  const handleCloseShare = () => {
    setOpenShare(false);
  };

  const handleChangeInvite = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInviteEmail(event.target.value);
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        anchor="right"
        BackdropProps={{
          invisible: true,
        }}
        PaperProps={{
          sx: { width: 320 },
        }}
        {...other}
      >
        <Scrollbar sx={{ height: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 2.5 }}
          >
            <Typography variant="h6"> Info </Typography>
          </Stack>
          <Stack
            spacing={2.5}
            justifyContent="center"
            sx={{ p: 2.5, bgcolor: 'background.neutral' }}
          >
            <FileThumbnail
              imageView
              file={type === 'folder' ? type : url}
              sx={{ width: 64, height: 64 }}
              imgSx={{ borderRadius: 1 }}
            />

            <Typography variant="h6" sx={{ wordBreak: 'break-all' }}>
              {name}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {description}
            </Typography>
            {item.fileType === 'folder' && <UploadThumbnail file={item} />}
            {item.fileType === 'file' &&
              (item.fileFormat === 'pdf' ||
                item.fileFormat === 'video' ||
                item.fileFormat === 'audio') && <UploadThumbnail file={item} />}
            <Divider sx={{ borderStyle: 'dashed' }} />

            {/* commented out for now https://trello.com/c/mOQezshw/50-hide-share-with-in-drawer-for-now */}
            {/*

			  <Stack spacing={1}>
			  <Panel
			  label="Tags"
			  toggle={toggleTags}
			  onToggle={handleToggleTags}
			  />

			  {toggleTags && (
			  <Autocomplete
			  multiple
			  freeSolo
			  limitTags={2}
			  options={item.tags.map((option) => option)}
			  value={tags}
			  onChange={(event, newValue) => {
			  setTags([
			  ...tags,
			  ...newValue.filter(
                          (option) => tags.indexOf(option) === -1
			  ),
			  ]);
			  }}
			  renderTags={(value: readonly string[], getTagProps) =>
			  value.map((option: string, index: number) => (
			  <Chip
                          {...getTagProps({ index })}
                          size="small"
                          label={option}
                          key={option}
			  />
			  ))
			  }
			  renderInput={(params) => (
			  <TextField {...params} placeholder="#Add a tags" />
			  )}
			  />
			  )}
			  </Stack>

			*/}

            <Stack spacing={1.5}>
              <Panel
                label="Properties"
                toggle={toggleProperties}
                onToggle={handleToggleProperties}
              />

              {toggleProperties && (
                <Stack spacing={1.5}>
                  <Row
                    label="Access"
                    value={accessTypesById[accessType].title}
                  />
                  <Row
                    label="State"
                    value={published ? 'Published' : 'Unpublished'}
                  />

                  <Row label="Size" value={fData(size)} />

                  <Row label="Modified" value={fDateTime(dateModified)} />

                  <Row label="Type" value={fileFormat(type)} />
                </Stack>
              )}
            </Stack>
          </Stack>
          <Box sx={{ p: 2.5 }}>
            <Button
              fullWidth
              color="primary"
              size="large"
              variant={'outlined'}
              startIcon={<Iconify icon="ic:baseline-edit" />}
              onClick={onEdit}
            >
              Edit properties
            </Button>
          </Box>
          {/* commented out for now https://trello.com/c/mOQezshw/50-hide-share-with-in-drawer-for-now */}
          {/*

		      <Stack
		      direction="row"
		      alignItems="center"
		      justifyContent="space-between"
		      sx={{ p: 2.5 }}
		      >
		      <Typography variant="subtitle2"> File Share With </Typography>

		      <IconButton
		      size="small"
		      color="success"
		      onClick={handleOpenShare}
		      sx={{
		      p: 0,
		      width: 24,
		      height: 24,
		      color: 'common.white',
		      bgcolor: 'success.main',
		      '&:hover': {
		      bgcolor: 'success.main',
		      },
		      }}
		      >
		      <Iconify icon="eva:plus-fill" />
		      </IconButton>
		      </Stack>

		    */}

          {hasShared && (
            <List disablePadding sx={{ pl: 2.5, pr: 1 }}>
              {shared.map((person) => (
                <FileInvitedItem key={person.id} person={person} />
              ))}
            </List>
          )}
        </Scrollbar>

        <RoleBasedGuard
          allowedRoles={[
            InviteMemberRequestDtoRoleEnum.Owner,
            InviteMemberRequestDtoRoleEnum.Admin,
            InviteMemberRequestDtoRoleEnum.Manager,
          ]}
        >
          <Box sx={{ p: 2.5 }}>
            <Button
              fullWidth
              color="error"
              size="large"
              startIcon={<Iconify icon="eva:trash-2-outline" />}
              onClick={onDelete}
            >
              Delete
            </Button>
          </Box>
        </RoleBasedGuard>
      </Drawer>

      <FileShareDialog
        open={openShare}
        shared={shared}
        inviteEmail={inviteEmail}
        onChangeInvite={handleChangeInvite}
        onCopyLink={onCopyLink}
        onClose={() => {
          handleCloseShare();
          setInviteEmail('');
        }}
      />
    </>
  );
};
export default FileDetailsDrawer;

// ----------------------------------------------------------------------

interface PanelProps extends StackProps {
  label: string;
  toggle: boolean;
  onToggle: VoidFunction;
}

function Panel({ label, toggle, onToggle, ...other }: PanelProps) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      {...other}
    >
      <Typography variant="subtitle2"> {label} </Typography>

      <IconButton size="small" onClick={onToggle}>
        <Iconify
          icon={toggle ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'}
        />
      </IconButton>
    </Stack>
  );
}

// ----------------------------------------------------------------------

type RowProps = {
  label: string;
  value: string;
};

function Row({ label, value = '' }: RowProps) {
  return (
    <Stack
      direction="row"
      sx={{ typography: 'caption', textTransform: 'capitalize' }}
    >
      <Box component="span" sx={{ width: 80, color: 'text.secondary', mr: 2 }}>
        {label}
      </Box>

      {value}
    </Stack>
  );
}
